import React, { useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import '../../styles/payment-modal.css';

const PaymentModal = ({ isOpen, onClose, onPaymentComplete }) => {
  const { 
    items, 
    total, 
    discount, 
    notes,
    customer,
    clearCart 
  } = useCart();
  
  const { user } = useAuth();
  
  const [step, setStep] = useState('payment'); // payment, processing, complete
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState(total);
  const [transactionCode, setTransactionCode] = useState('');
  const [mpesaPhone, setMpesaPhone] = useState(customer?.phone || '');
  const [mpesaStatus, setMpesaStatus] = useState(''); // '', 'pushing', 'waiting'
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const change = Math.max(0, amountPaid - total);
  const isShort = amountPaid < total && paymentMethod === 'cash';

  const quickAmounts = [100, 500, 1000, 2000, 5000];

  const handlePayment = async () => {
    if (paymentMethod === 'cash' && amountPaid < total) {
      toast.error('Insufficient payment amount');
      return;
    }

    if (paymentMethod === 'mpesa' && !transactionCode && mpesaStatus !== 'waiting') {
      toast.error('Please enter M-Pesa transaction code or initiate STK Push');
      return;
    }

    if ((paymentMethod === 'credit' || amountPaid < total) && !customer) {
      toast.error('A customer must be selected for credit/partial sales');
      setStep('payment');
      return;
    }

    setProcessing(true);
    setStep('processing');

    try {
      const saleData = {
        items: items.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.selling_price
        })),
        customer_id: customer?.id,
        payment_method: paymentMethod,
        amount_paid: amountPaid,
        transaction_code: transactionCode,
        mpesa_phone: mpesaPhone,
        discount_total: discount,
        notes: notes
      };

      const response = await api.post('/pos.php', saleData);
      
      if (response.data.success) {
        setStep('complete');
        toast.success('Sale completed successfully!');
        
        // Pass sale data back to POSInterface to show receipt modal
        if (onPaymentComplete) {
          onPaymentComplete(response.data.sale);
        }
        
        // Clear cart after 3 seconds (or immediately since user will see receipt)
        setTimeout(() => {
          clearCart();
          onClose();
          setStep('payment');
        }, 1500);
      }
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error(error.response?.data?.error || 'Payment failed');
      setStep('payment');
    } finally {
      setProcessing(false);
    }
  };

  // Receipt rendering is now handled by ReceiptModal

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount).replace('KES', 'KSh');
  };

  if (step === 'complete') {
    return (
      <div className="modal-overlay">
        <div className="modal-content payment-modal success-modal">
          <div className="success-animation">
            <div className="checkmark">✓</div>
          </div>
          <h2>Payment Successful!</h2>
          <p>Sale completed successfully</p>
          <button className="close-btn" onClick={() => {
            clearCart();
            onClose();
            setStep('payment');
          }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div className="modal-overlay">
        <div className="modal-content payment-modal processing-modal">
          <div className="spinner"></div>
          <h2>Processing Payment</h2>
          <p>Please wait...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={(e) => e.target.classList.contains('modal-overlay') && onClose()}>
      <div className="modal-content payment-modal">
        <div className="modal-header">
          <h2>Complete Payment</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="total-display">
            <span className="total-label">Total Amount:</span>
            <span className="total-amount">{formatCurrency(total)}</span>
          </div>

          {customer && (
            <div className="customer-info">
              <p><strong>Customer:</strong> {customer.first_name} {customer.last_name}</p>
              {customer.current_balance > 0 && (
                <p className="balance-warning">
                  <strong>Outstanding Balance:</strong> {formatCurrency(customer.current_balance)}
                </p>
              )}
            </div>
          )}

          <div className="payment-methods">
            <button 
              className={`method-btn ${paymentMethod === 'cash' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('cash')}
            >
              <span className="method-icon">💵</span>
              Cash
            </button>
            <button 
              className={`method-btn ${paymentMethod === 'mpesa' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('mpesa')}
            >
              <span className="method-icon">📱</span>
              M-Pesa
            </button>
            <button 
              className={`method-btn ${paymentMethod === 'card' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('card')}
            >
              <span className="method-icon">💳</span>
              Card
            </button>
            <button 
              className={`method-btn ${paymentMethod === 'credit' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('credit')}
              disabled={!customer}
              title={!customer ? "You must attach a customer to the sale to use Credit" : "Credit Sale"}
            >
              <span className="method-icon">📝</span>
              Credit
            </button>
          </div>
          
          {!customer && (
            <div style={{ color: '#ef4444', fontSize: '0.85rem', textAlign: 'center', marginTop: '10px' }}>
              * You must attach a Customer to this sale before you can use Credit.
            </div>
          )}

          {paymentMethod === 'cash' && (
            <div className="cash-payment">
              <div className="form-group">
                <label>Amount Paid</label>
                <input
                  type="number"
                  value={amountPaid === 0 ? '' : amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  min={total}
                  step="any"
                  autoFocus
                  className="amount-input"
                />
              </div>

              <div className="quick-amounts">
                {quickAmounts.map(amount => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setAmountPaid(amount)}
                    className="quick-amount-btn"
                  >
                    {formatCurrency(amount)}
                  </button>
                ))}
              </div>

              {!isShort && (
                <div className="change-display">
                  <span className="change-label">Change:</span>
                  <span className="change-amount">{formatCurrency(change)}</span>
                </div>
              )}

              {isShort && (
                <div className="short-warning">
                  Payment is short by {formatCurrency(total - amountPaid)}
                </div>
              )}
            </div>
          )}

          {paymentMethod === 'mpesa' && (
            <div className="mpesa-payment">
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label>Send Prompt to Phone (STK Push)</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={mpesaPhone}
                    onChange={(e) => setMpesaPhone(e.target.value)}
                    placeholder="e.g., 0712345678"
                    pattern="[0-9]{10,12}"
                  />
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={async () => {
                      if (!mpesaPhone) return toast.error('Enter phone number');
                      setMpesaStatus('pushing');
                      try {
                        const res = await api.post('/mpesa.php/stkpush', {
                          phone: mpesaPhone,
                          amount: total
                        });
                        toast.success('Prompt sent to customer phone!');
                        setMpesaStatus('waiting');
                      } catch (err) {
                        toast.error(err.response?.data?.error || 'Failed to send prompt');
                        setMpesaStatus('');
                      }
                    }}
                    disabled={mpesaStatus === 'pushing' || mpesaStatus === 'waiting'}
                  >
                    {mpesaStatus === 'pushing' ? 'Sending...' : mpesaStatus === 'waiting' ? 'Prompt Sent' : 'Send Prompt'}
                  </button>
                </div>
                {mpesaStatus === 'waiting' && (
                  <p className="help-text" style={{ color: 'var(--primary-color)' }}>
                    Waiting for customer to enter PIN. Once confirmed, you can complete the sale.
                  </p>
                )}
              </div>
              
              <div className="form-separator" style={{ textAlign: 'center', margin: '15px 0', borderBottom: '1px solid #ddd', lineHeight: '0.1em' }}>
                <span style={{ background: '#fff', padding: '0 10px', color: '#888' }}>OR MANUAL ENTRY</span>
              </div>

              <div className="form-group">
                <label>M-Pesa Transaction Code</label>
                <input
                  type="text"
                  value={transactionCode}
                  onChange={(e) => setTransactionCode(e.target.value.toUpperCase())}
                  placeholder="e.g., QW1234XYZ"
                  pattern="[A-Z0-9]{10,}"
                />
                <p className="help-text">
                  Enter the transaction code from the customer's M-Pesa message
                </p>
              </div>
            </div>
          )}

          {paymentMethod === 'credit' && customer && (
            <div className="credit-payment">
              <div className="credit-info">
                <p>This will create a credit sale for this customer</p>
                <div className="credit-details">
                  <div className="credit-row">
                    <span>Credit Limit:</span>
                    <span>{formatCurrency(Number(customer.credit_limit) || 0)}</span>
                  </div>
                  <div className="credit-row">
                    <span>Current Balance:</span>
                    <span>{formatCurrency(Number(customer.current_balance) || 0)}</span>
                  </div>
                  <div className="credit-row">
                    <span>New Balance:</span>
                    <span className="new-balance">
                      {formatCurrency((Number(customer.current_balance) || 0) + total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button className="secondary-button" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="primary-button"
              onClick={handlePayment}
              disabled={
                processing ||
                (paymentMethod === 'cash' && amountPaid < total) ||
                (paymentMethod === 'mpesa' && !transactionCode)
              }
            >
              {processing ? 'Processing...' : 'Complete Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;